import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiBaseUrl } from '../utils/apiConfig';

const SubscriptionManager = ({ user, onSubscriptionUpdate }) => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (user && user.id) {
      fetchSubscription();
    }
  }, [user]);

  const fetchSubscription = async () => {
    try {
      setLoading(true);
      
      // Get token from localStorage
      const token = user.token || localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const API_BASE_URL = getApiBaseUrl();
      const response = await fetch(`${API_BASE_URL}/api/subscription/${user.id}`, { headers });
      if (response.ok) {
        const data = await response.json();
        console.log('Subscription data received:', data);
        setSubscription(data.subscription);
      } else if (response.status === 404) {
        console.log('No subscription found for user, setting to null');
        setSubscription(null);
      } else {
        console.error('Error fetching subscription:', response.status, response.statusText);
        setSubscription(null);
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscriptionClick = (planType, durationDays, planData) => {
    // Redirect to payment page instead of creating subscription directly
    navigate('/payment', { 
      state: { 
        plan: {
          id: planType,
          name: planData.name,
          price: planData.price,
          priceNrs: planData.priceNrs,
          period: planData.period,
          description: planData.description,
          features: planData.features,
          savings: planData.savings,
          originalPrice: planData.originalPrice
        }
      }
    });
  };

  const cancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription?')) {
      return;
    }

    try {
      // Get token from localStorage
      const token = user.token || localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch('http://localhost:5000/api/subscriptions/cancel', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          user_id: user.id
        })
      });

      if (response.ok) {
        fetchSubscription(); // Refresh subscription data
        alert('Subscription cancelled successfully');
      } else {
        alert('Failed to cancel subscription');
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      alert('Error cancelling subscription');
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 0: return 'Inactive';
      case 1: return 'Active';
      case 2: return 'Expired';
      case 3: return 'Cancelled';
      default: return 'Unknown';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 1: return 'text-green-600 bg-green-100';
      case 2: return 'text-red-600 bg-red-100';
      case 3: return 'text-orange-600 bg-orange-100'; // Cancelled status
      default: return 'text-yellow-600 bg-yellow-100';
    }
  };

  const getDaysRemaining = (endDate) => {
    const end = new Date(endDate);
    const now = new Date();
    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="text-center">Loading subscription information...</div>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Subscription Management</h2>

      {subscription && subscription.id !== -1 ? (
        <div className="space-y-6">
          {/* Current Subscription */}
          <div className="border rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Subscription</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Plan Type</label>
                <p className="text-lg font-semibold text-blue-600 capitalize">{subscription.plan_type}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(subscription.status)}`}>
                  {getStatusText(subscription.status)}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Start Date</label>
                <p className="text-gray-900">{new Date(subscription.start_date).toLocaleDateString()}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">End Date</label>
                <p className="text-gray-900">{new Date(subscription.end_date).toLocaleDateString()}</p>
                {subscription.status === 1 && (
                  <p className="text-sm text-gray-600">
                    {getDaysRemaining(subscription.end_date)} days remaining
                  </p>
                )}
              </div>
            </div>

            {subscription.status === 1 && (
              <div className="mt-4">
                <button
                  onClick={cancelSubscription}
                  className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700"
                >
                  Cancel Subscription
                </button>
              </div>
            )}

            {/* Show different content based on subscription status */}
            {subscription.status === 3 && (
              <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <h4 className="text-lg font-semibold text-orange-800 mb-2">Subscription Cancelled</h4>
                <p className="text-orange-700 mb-3">
                  You have cancelled your previous subscription. All premium features are now locked.
                  You can only view content but cannot access premium games and features.
                </p>
                <p className="text-sm text-orange-600">
                  Your subscription was active until: {new Date(subscription.end_date).toLocaleDateString()}
                </p>
              </div>
            )}

            {subscription.status === 2 && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <h4 className="text-lg font-semibold text-red-800 mb-2">Subscription Expired</h4>
                <p className="text-red-700 mb-3">
                  Your subscription has expired. All premium features are now locked.
                </p>
              </div>
            )}
          </div>

          {/* Upgrade Options */}
          {subscription.plan_type === 'free' && subscription.status === 1 && (
            <div className="border rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Upgrade to Premium</h3>
              <div className="bg-gradient-to-r from-purple-500 to-blue-600 text-white p-4 rounded-lg">
                <h4 className="text-xl font-bold mb-2">Premium Plan</h4>
                <ul className="space-y-1 text-sm mb-4">
                  <li>• Access to all math games</li>
                  <li>• Advanced progress tracking</li>
                  <li>• Priority support</li>
                  <li>• Exclusive content</li>
                </ul>
                <button
                  onClick={() => handleSubscriptionClick('premium', 30, {
                    name: 'Premium Plan',
                    price: 'NRs. 1,800',
                    priceNrs: 1800,
                    period: '30 days',
                    description: 'Full premium access',
                    features: ['All Premium Features']
                  })}
                  className="bg-white text-purple-600 px-4 py-2 rounded-md font-semibold hover:bg-gray-100"
                >
                  Upgrade Now - 30 Days
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* No Subscription */
        <div className="space-y-6">
          <div className="text-center py-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Active Subscription</h3>
            <p className="text-gray-600 mb-6">Choose a plan to get started with MathVenture!</p>
          </div>

          {/* Subscription Plans */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Trial Plan */}
            <div className="border-2 border-green-200 rounded-xl p-8 bg-gradient-to-br from-green-50 to-green-100">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">4-Day Trial</h3>
                <div className="mb-4">
                  <span className="text-4xl font-bold text-green-600">Free</span>
                  <span className="text-lg text-gray-600 ml-2">/4 days</span>
                </div>
                <p className="text-gray-600">Try all premium features for free</p>
              </div>
              
              <div className="mb-8">
                <h4 className="font-semibold text-gray-900 mb-4 text-lg">Features included:</h4>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">Full access to Grade 4 & 5 Games</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">Advanced Math Topics</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">Progress Analytics</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">AI Tutoring</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">All Premium Features</span>
                  </li>
                </ul>
              </div>
              
              <button
                onClick={() => handleSubscriptionClick('trial', 4, {
                  name: '4-Day Trial',
                  price: 'Free',
                  priceNrs: 0,
                  period: '4 days',
                  description: 'Try all premium features for free',
                  features: [
                    'Full access to Grade 4 & 5 Games',
                    'Advanced Math Topics',
                    'Progress Analytics',
                    'AI Tutoring',
                    'All Premium Features'
                  ]
                })}
                className="w-full bg-green-600 text-white py-4 px-6 rounded-lg font-semibold text-lg hover:bg-green-700 transition-colors"
              >
                Start Free Trial
              </button>
            </div>

            {/* Premium Plans */}
            <div className="space-y-6">
              {/* Quarterly Plan */}
              <div className="border-2 border-purple-200 rounded-xl p-6 bg-gradient-to-br from-purple-50 to-purple-100">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Quarterly Plan</h3>
                    <div className="flex items-baseline">
                      <span className="text-3xl font-bold text-purple-600">NRs. 4,500</span>
                      <span className="text-sm text-gray-600 ml-2">/3 months</span>
                    </div>
                    <p className="text-sm text-gray-500 line-through">NRs. 5,400</p>
                  </div>
                  <span className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold">17% OFF</span>
                </div>
                <button
                  onClick={() => handleSubscriptionClick('quarterly', 90, {
                    name: 'Quarterly Plan',
                    price: 'NRs. 4,500',
                    priceNrs: 4500,
                    period: '3 months',
                    description: 'Perfect for short-term learning goals',
                    features: [
                      'Full access to Grade 4 & 5 Games',
                      'Advanced Math Topics',
                      'Progress Analytics',
                      'AI Tutoring',
                      'All Premium Features'
                    ],
                    savings: '17% OFF',
                    originalPrice: 'NRs. 5,400'
                  })}
                  className="w-full bg-purple-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-purple-700 transition-colors"
                >
                  Subscribe Now
                </button>
              </div>

              {/* Half-Yearly Plan */}
              <div className="border-2 border-blue-200 rounded-xl p-6 bg-gradient-to-br from-blue-50 to-blue-100 relative">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-blue-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                    MOST POPULAR
                  </span>
                </div>
                <div className="flex justify-between items-start mb-4 mt-2">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Half-Yearly Plan</h3>
                    <div className="flex items-baseline">
                      <span className="text-3xl font-bold text-blue-600">NRs. 8,000</span>
                      <span className="text-sm text-gray-600 ml-2">/6 months</span>
                    </div>
                    <p className="text-sm text-gray-500 line-through">NRs. 10,800</p>
                  </div>
                  <span className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold">26% OFF</span>
                </div>
                <button
                  onClick={() => handleSubscriptionClick('halfyearly', 180, {
                    name: 'Half-Yearly Plan',
                    price: 'NRs. 8,000',
                    priceNrs: 8000,
                    period: '6 months',
                    description: 'Most popular choice for consistent learning',
                    features: [
                      'Full access to Grade 4 & 5 Games',
                      'Advanced Math Topics',
                      'Progress Analytics',
                      'AI Tutoring',
                      'All Premium Features'
                    ],
                    savings: '26% OFF',
                    originalPrice: 'NRs. 10,800'
                  })}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-4 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-colors"
                >
                  Subscribe Now
                </button>
              </div>

              {/* Yearly Plan */}
              <div className="border-2 border-green-200 rounded-xl p-6 bg-gradient-to-br from-green-50 to-teal-50">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Yearly Plan</h3>
                    <div className="flex items-baseline">
                      <span className="text-3xl font-bold text-green-600">NRs. 15,000</span>
                      <span className="text-sm text-gray-600 ml-2">/12 months</span>
                    </div>
                    <p className="text-sm text-gray-500 line-through">NRs. 21,600</p>
                  </div>
                  <span className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold">31% OFF</span>
                </div>
                <button
                  onClick={() => handleSubscriptionClick('yearly', 365, {
                    name: 'Yearly Plan',
                    price: 'NRs. 15,000',
                    priceNrs: 15000,
                    period: '12 months',
                    description: 'Best value for long-term learning commitment',
                    features: [
                      'Full access to Grade 4 & 5 Games',
                      'Advanced Math Topics',
                      'Progress Analytics',
                      'AI Tutoring',
                      'All Premium Features'
                    ],
                    savings: '31% OFF',
                    originalPrice: 'NRs. 21,600'
                  })}
                  className="w-full bg-gradient-to-r from-green-600 to-teal-600 text-white py-3 px-4 rounded-lg font-semibold hover:from-green-700 hover:to-teal-700 transition-colors"
                >
                  Subscribe Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionManager;